'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from '@/components/ui/use-toast';
import { formatCurrency } from '@/components/home/format';
import { handleGlobalError } from '@/utils/error-handler';
import { formatDateForUser } from '@/utils/formatters';
import { logError } from '@/utils/logger';
import {
  createOrganizedMatch,
  deleteOrganizedMatch,
  editOrganizedMatch,
} from '@/api/match/organizedMatchApi';
import type { Sport } from '@/types/sports.model';
import type { OrganizedMatch } from '@/types/organized-match.model';
import { OrganizedMatchForm } from './OrganizedMatchForm';
import {
  buildCreatePayload,
  buildEditPayload,
  hasPlayers,
  defaultPlayersPerTeam,
  effectiveQuantity,
  scheduleOf,
  summarizeBatch,
  type OrganizedMatchFormValues,
  type ScheduleValues,
} from './organized-match-form-model';
import {
  capacity,
  filled,
  matchStatus,
  matchStatusLabel,
  matchTypeLabel,
  turnEnd,
} from './turnos-model';

export type SheetState =
  | {
      mode: 'create';
      clubId: string;
      startDate: string;
      endDate: string;
      /** Courts still free in the slot, by sport uid. */
      freeCourts: Record<string, number>;
    }
  | { mode: 'view' | 'edit'; match: OrganizedMatch };

interface OrganizedMatchSheetProps {
  state: SheetState | null;
  sports: Sport[];
  fee: number | null;
  /** Club-local "yyyy-MM-ddTHH:mm". */
  now: string;
  currentUserEmail?: string;
  onModeChange: (state: SheetState) => void;
  onClose: () => void;
  /** Called after any change so the page refetches the week. */
  onChanged: () => void;
}

const displayDate = (iso: string) => formatDateForUser(iso, 'dd/MM/yyyy');

function defaultValues(sports: Sport[]): OrganizedMatchFormValues {
  return {
    sportUid: sports[0]?.uid ?? '',
    playersPerTeam: defaultPlayersPerTeam(sports[0]),
    matchType: 'FRIENDLY',
    gender: 'ANY',
    notes: '',
    players: [],
    quantity: 1,
  };
}

function valuesOf(match: OrganizedMatch): OrganizedMatchFormValues {
  return {
    sportUid: match.sport.uid,
    playersPerTeam: match.playersPerTeam,
    matchType: match.matchType,
    gender: match.gender,
    notes: match.notes ?? '',
    players: [],
    quantity: 1,
  };
}

function MatchDetails({ match }: { match: OrganizedMatch }) {
  const players = [...match.team1.players, ...match.team2.players];
  const rows: [string, string][] = [
    ['Fecha', displayDate(match.startDate)],
    ['Hora', `${match.startDate.slice(11, 16)} a ${match.endDate.slice(11, 16)}`],
    ['Deporte', match.sport.name],
    ['Tipo', matchTypeLabel(match.matchType)],
    ['Categoría', match.category?.name ?? '—'],
    ['Estado', matchStatusLabel(matchStatus(match))],
  ];
  return (
    <div className="flex flex-col gap-3 text-sm">
      <dl className="grid grid-cols-[110px_1fr] gap-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <div>
        <p className="text-muted-foreground">
          Jugadores anotados ({filled(match)}/{capacity(match)})
        </p>
        {players.length > 0 ? (
          <ul className="mt-1 list-disc pl-5">
            {players.map((p) => (
              <li key={p.email}>{p.name || p.email}</li>
            ))}
          </ul>
        ) : (
          <p className="mt-1">Todavía no se anotó nadie.</p>
        )}
      </div>
      {match.notes && (
        <p>
          <span className="text-muted-foreground">Notas: </span>
          {match.notes}
        </p>
      )}
      {match.activeChargeAmount != null && (
        <p className="rounded-md bg-muted p-2 text-xs">
          {`Cargo de ${formatCurrency(match.activeChargeAmount)} vigente`}
        </p>
      )}
    </div>
  );
}

export function OrganizedMatchSheet({
  state,
  sports,
  fee,
  now,
  currentUserEmail,
  onModeChange,
  onClose,
  onChanged,
}: OrganizedMatchSheetProps) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleCreate = async (values: OrganizedMatchFormValues) => {
    if (state?.mode !== 'create') return;
    setSubmitting(true);
    try {
      const payload = buildCreatePayload(values, state.clubId, state.startDate, state.endDate);
      const results = await Promise.allSettled(
        Array.from({ length: effectiveQuantity(values) }, () => createOrganizedMatch(payload)),
      );
      results.forEach((r) => r.status === 'rejected' && logError('OrganizedMatchSheet.create', r.reason));
      const { created, failed, firstError } = summarizeBatch(results);
      const total = created + failed;
      if (failed === 0) {
        toast({ title: created === 1 ? 'Se creó 1 partido' : `Se crearon ${created} partidos` });
      } else {
        const reason = firstError ?? 'Probá de nuevo en unos segundos.';
        toast(
          total === 1
            ? { title: 'No se pudo crear el partido', description: reason, variant: 'destructive' }
            : {
                title: `Se crearon ${created} de ${total}: ${reason}`,
                variant: 'destructive',
              },
        );
      }
      if (created > 0) {
        onChanged();
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (
    values: OrganizedMatchFormValues,
    schedule: ScheduleValues | undefined,
  ) => {
    if (state?.mode !== 'edit' || !schedule) return;
    setSubmitting(true);
    try {
      const payload = buildEditPayload(
        state.match,
        values,
        `${schedule.date}T${schedule.time}:00`,
        turnEnd(schedule.date, schedule.time, schedule.durationMinutes),
      );
      await editOrganizedMatch(state.match.id, payload);
      toast({ title: 'Partido actualizado' });
      onChanged();
      onClose();
    } catch (err) {
      handleGlobalError(
        err,
        'No se pudo guardar el partido',
        'Probá de nuevo en unos segundos.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (state?.mode !== 'view') return;
    setConfirmDelete(false);
    setSubmitting(true);
    try {
      await deleteOrganizedMatch(state.match.id);
      toast({ title: 'Partido borrado' });
      onChanged();
      onClose();
    } catch (err) {
      handleGlobalError(
        err,
        'No se pudo borrar el partido',
        'Probá de nuevo en unos segundos.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isCreator =
    state?.mode === 'view' &&
    !!currentUserEmail &&
    state.match.createdBy?.email?.toLowerCase() === currentUserEmail.toLowerCase();

  const deleteDescription = [
    'Se borra el partido y sus jugadores anotados dejan de verlo.',
    state?.mode === 'view' && state.match.activeChargeAmount != null
      ? state.match.startDate.slice(0, 16) > now
        ? 'El cargo en tu cuenta corriente se reintegra.'
        : 'Como el partido ya empezó, el cargo no se reintegra.'
      : null,
    '¿Querés continuar?',
  ]
    .filter(Boolean)
    .join(' ');

  // Only the sports with a free court in the slot can get a new match there.
  const createSports =
    state?.mode === 'create' ? sports.filter((s) => (state.freeCourts[s.uid] ?? 0) > 0) : sports;

  const title =
    state?.mode === 'create'
      ? 'Nuevo partido'
      : state?.mode === 'edit'
        ? 'Editar partido'
        : 'Partido';

  return (
    <>
      <Sheet open={state !== null} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          className="w-full overflow-y-auto sm:max-w-md"
          overlayClassName="bg-black/30"
        >
          <SheetHeader>
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>
              {state?.mode === 'create'
                ? `${displayDate(state.startDate)} · ${state.startDate.slice(11, 16)} a ${state.endDate.slice(11, 16)}`
                : state
                  ? `${displayDate(state.match.startDate)} · ${state.match.startDate.slice(11, 16)}`
                  : ''}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {state?.mode === 'create' && (
              <OrganizedMatchForm
                key={`create-${state.startDate}`}
                mode="create"
                sports={createSports}
                initial={defaultValues(createSports)}
                freeCourts={state.freeCourts}
                fee={fee}
                submitting={submitting}
                currentUserEmail={currentUserEmail}
                onSubmit={handleCreate}
              />
            )}
            {state?.mode === 'view' && (
              <div className="flex flex-col gap-4">
                <MatchDetails match={state.match} />
                {isCreator && (
                  <div className="flex gap-2">
                    {/* A match that already started can no longer be moved or edited. */}
                    {state.match.startDate.slice(0, 16) > now && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => onModeChange({ mode: 'edit', match: state.match })}
                      >
                        Editar
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={submitting}
                      onClick={() => setConfirmDelete(true)}
                    >
                      Borrar
                    </Button>
                  </div>
                )}
              </div>
            )}
            {state?.mode === 'edit' && (
              <OrganizedMatchForm
                key={`edit-${state.match.id}`}
                mode="edit"
                sports={sports}
                initial={valuesOf(state.match)}
                initialSchedule={scheduleOf(state.match)}
                lockedFields={hasPlayers(state.match)}
                fee={fee}
                now={now}
                submitting={submitting}
                onSubmit={handleEdit}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
      <ConfirmationModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Borrar partido"
        description={deleteDescription}
        confirmText="Borrar"
      />
    </>
  );
}
